/**
 * Sales Dual-Write Test Suite
 *
 * This utility automatically creates sales with various configurations
 * and verifies that Supabase and Firestore are in sync.
 *
 * Usage:
 *   import { runSalesDualWriteTests } from './utils/salesDualWriteTest';
 *   await runSalesDualWriteTests(db, testConfig);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import {
  saveNewSale,
  deleteSale,
  updateSale,
  markSalePaid,
  saveSaleReturn,
  deleteSaleReturn,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { salesApi } from '../global/API/typedApiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, productsDictAtom, dbAtom, userIdAtom, constantsAtom } from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * Generate test sale data for a specific variation
 */
export function generateTestSale(variation, testData) {
  const {
    organizationId,
    storeId,
    products,
    clients,
    paymentMethods,
    purchaseMethods,
    priceTypes,
  } = testData;

  // Base sale data - use actual Date objects for Firestore compatibility
  const now = new Date();

  // Default purchase method (required for dailyStatIncrementsForSales)
  const defaultPurchaseMethod = purchaseMethods?.find(p => p.id === 'shop-offline' || p.firestoreId === 'shop-offline') || {
    id: 'shop-offline',
    firestoreId: 'shop-offline',
    name: 'Shop',
    icon: 'house-user',
    iconText: 'Shop',
  };

  const baseSale = {
    organizationId,
    store: storeId,
    saleDate: now,
    dailyStatID: `test-${Date.now()}-${storeId}`,
    cashierUID: 'test-cashier',
    totalCartPrice: 0,
    finalTotalCartPrice: 0,
    totalPurchasePrice: 0,
    cartItems: [],
    // Default purchase method (in-store)
    purchaseMethod: defaultPurchaseMethod,
    // Default to no client, no discounts
    clientId: null,
    client: null,
    discountAmount: 0,
    discountPercentage: 0,
    punchCardEnabled: false,
    punchCardCapacity: null,
    punchCardReduction: 0,
    cashbackEnabled: false,
    bonusAccumulation: 0,
    bonusReduction: 0,
    bonusAction: null,
    comment: '',
    tableNumber: null,
    saleType: 'product', // Default sale type
  };

  // Apply variation
  switch (variation.category) {
    case 'client':
      return applyClientVariation(baseSale, variation, testData);
    case 'payment':
      return applyPaymentVariation(baseSale, variation, testData);
    case 'cart':
      return applyCartVariation(baseSale, variation, testData);
    case 'discount':
      return applyDiscountVariation(baseSale, variation, testData);
    case 'purchaseMethod':
      return applyPurchaseMethodVariation(baseSale, variation, testData);
    case 'delivery':
      return applyDeliveryVariation(baseSale, variation, testData);
    case 'optional':
      return applyOptionalFieldsVariation(baseSale, variation, testData);
    case 'date':
      return applyDateVariation(baseSale, variation, testData);
    case 'lifecycle':
      // Lifecycle tests are handled differently - they create, modify, then verify
      return applyLifecycleVariation(baseSale, variation, testData);
    case 'cascading':
      // Cascading tests verify side effects (movements, transactions, etc.)
      return applyCascadingVariation(baseSale, variation, testData);
    default:
      return baseSale;
  }
}

function applyClientVariation(sale, variation, testData) {
  const { clients, storeId } = testData;

  // Add a default product
  sale.cartItems = [createTestCartItem(testData.products[0], storeId, 1)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.finalTotalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;

  // Add default payment
  sale.paymentMethod = testData.paymentMethods.find(p => p.isCash) || testData.paymentMethods[0];

  switch (variation.id) {
    case '1.1': // No client
      // Already set to null
      break;

    case '1.2': // With client (no loyalty)
      if (clients.length > 0) {
        const client = clients.find(c => !c.cashbackEnabled && !c.punchCardEnabled) || clients[0];
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
      }
      break;

    case '1.3': // With client + cashback enabled
      if (clients.length > 0) {
        const client = clients[0];
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
      }
      // Set cashback fields regardless of client for dual-write testing
      sale.cashbackEnabled = true;
      sale.bonusAccumulation = Math.round(sale.finalTotalCartPrice * 0.05); // 5% cashback
      break;

    case '1.4': // With client + bonus reduction
      if (clients.length > 0) {
        const client = { ...clients[0], bonusValue: 100 };
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
      }
      // Set bonus reduction fields regardless of client for dual-write testing
      sale.cashbackEnabled = true;
      sale.bonusReduction = 50;
      sale.bonusAction = 1;
      sale.finalTotalCartPrice = sale.totalCartPrice - sale.bonusReduction;
      break;

    case '1.5': // With client + punch card enabled
      if (clients.length > 0) {
        const client = clients[0];
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
      }
      // Set punch card fields regardless of client for dual-write testing
      sale.punchCardEnabled = true;
      sale.punchCardCapacity = 7;
      // Mark cart item as punch card enabled
      sale.cartItems[0].punchCardEnabled = true;
      break;

    case '1.6': // With client + punch card reduction (free item)
      if (clients.length > 0) {
        const client = { ...clients[0], punchCardValue: 6 }; // Almost full
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
      }
      // Set punch card reduction fields regardless of client for dual-write testing
      sale.punchCardEnabled = true;
      sale.punchCardCapacity = 7;
      sale.punchCardReduction = sale.cartItems[0].finalItemPrice; // Free item
      sale.cartItems[0].punchCardEnabled = true;
      sale.finalTotalCartPrice = sale.totalCartPrice - sale.punchCardReduction;
      break;

    case '1.7': // With client + loyalty discount
      if (clients.length > 0) {
        const client = { ...clients[0], loyaltyDiscount: { percentage: 10 } };
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
      }
      // Set loyalty discount fields regardless of client for dual-write testing
      sale.discountPercentage = 10;
      sale.discountAmount = Math.round(sale.totalCartPrice * 0.1);
      sale.finalTotalCartPrice = sale.totalCartPrice - sale.discountAmount;
      break;
  }

  return sale;
}

function applyPaymentVariation(sale, variation, testData) {
  const { paymentMethods, storeId } = testData;

  // Add a default product
  sale.cartItems = [createTestCartItem(testData.products[0], storeId, 1)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.finalTotalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;

  const cashMethod = paymentMethods.find(p => p.isCash);
  const cardMethod = paymentMethods.find(p => !p.isCash);

  switch (variation.id) {
    case '2.1': // Cash payment
      sale.paymentMethod = cashMethod || paymentMethods[0];
      break;

    case '2.2': // Card/Kaspi payment
      sale.paymentMethod = cardMethod || paymentMethods[0];
      break;

    case '2.3': // Split payment
      sale.paymentMethod = {
        ...cashMethod,
        value: Math.round(sale.finalTotalCartPrice / 2),
      };
      sale.secondPaymentMethod = {
        ...cardMethod,
        value: sale.finalTotalCartPrice - sale.paymentMethod.value,
        destinationWalletForStore: cardMethod?.destinationWalletForStore,
      };
      break;

    case '2.4': // Pay later (no payment)
      sale.paymentMethod = null;
      break;
  }

  return sale;
}

function applyCartVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods } = testData;

  sale.paymentMethod = paymentMethods[0];

  switch (variation.id) {
    case '3.1': // Single product
      sale.cartItems = [createTestCartItem(products[0], storeId, 1)];
      break;

    case '3.2': // Multiple products
      sale.cartItems = products.slice(0, 3).map((p, i) =>
        createTestCartItem(p, storeId, i + 1)
      );
      break;

    case '3.3': // Service/custom item only
      sale.cartItems = [{
        id: 'customProduct',
        name: 'Test Service',
        quantity: 1,
        price: 5000,
        finalItemPrice: 5000,
        itemPriceWithoutModifiers: 5000,
        purchasePrice: 0,
        punchCardEnabled: false,
      }];
      break;

    case '3.4': // Mixed (products + services)
      sale.cartItems = [
        createTestCartItem(products[0], storeId, 1),
        {
          id: 'customProduct',
          name: 'Additional Service',
          quantity: 1,
          price: 2000,
          finalItemPrice: 2000,
          itemPriceWithoutModifiers: 2000,
          purchasePrice: 0,
          punchCardEnabled: false,
        },
      ];
      break;

    case '3.5': { // Product with variant
      const productWithVariant = { ...products[0] };
      sale.cartItems = [{
        ...createTestCartItem(productWithVariant, storeId, 1),
        variant: { size: 'L', color: 'Red' },
      }];
      break;
    }

    case '3.6': // Item with punch card enabled
      sale.cartItems = [{
        ...createTestCartItem(products[0], storeId, 1),
        punchCardEnabled: true,
      }];
      sale.punchCardEnabled = true;
      sale.punchCardCapacity = 7;
      break;

    case '3.7': // Large quantity
      sale.cartItems = [createTestCartItem(products[0], storeId, 100)];
      break;
  }

  // Calculate totals
  sale.totalCartPrice = sale.cartItems.reduce((sum, item) => sum + (item.finalItemPrice || 0), 0);
  sale.finalTotalCartPrice = sale.totalCartPrice;
  sale.totalPurchasePrice = sale.cartItems.reduce((sum, item) =>
    sum + ((item.purchasePrice || 0) * (item.quantity || 0)), 0
  );

  return sale;
}

function applyDiscountVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods } = testData;

  // Add a default product
  sale.cartItems = [createTestCartItem(products[0], storeId, 1)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;
  sale.paymentMethod = paymentMethods[0];

  switch (variation.id) {
    case '4.1': // No discount
      sale.finalTotalCartPrice = sale.totalCartPrice;
      break;

    case '4.2': // Percentage discount (10%)
      sale.discountPercentage = 10;
      sale.discountAmount = Math.round(sale.totalCartPrice * 0.1);
      sale.finalTotalCartPrice = sale.totalCartPrice - sale.discountAmount;
      break;

    case '4.3': // Fixed amount discount
      sale.discountAmount = 500;
      sale.finalTotalCartPrice = sale.totalCartPrice - sale.discountAmount;
      break;

    case '4.4': // Total discount fields
      sale.totalDiscount = 1000;
      sale.totalDiscountPercent = 20;
      sale.finalTotalCartPrice = sale.totalCartPrice - sale.totalDiscount;
      break;
  }

  return sale;
}

function applyPurchaseMethodVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods, purchaseMethods } = testData;

  // Add a default product
  sale.cartItems = [createTestCartItem(products[0], storeId, 1)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.finalTotalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;
  sale.paymentMethod = paymentMethods[0];

  switch (variation.id) {
    case '6.1': // Shop offline (in-store)
      sale.purchaseMethod = purchaseMethods?.find(p => p.id === 'shop-offline' || p.firestoreId === 'shop-offline') || {
        id: 'shop-offline',
        firestoreId: 'shop-offline',
        name: 'Shop',
        icon: 'house-user',
        iconText: 'Shop',
      };
      break;

    case '6.2': // Delivery purchase method
      sale.purchaseMethod = purchaseMethods?.find(p => p.id === 'delivery' || p.firestoreId === 'delivery') || {
        id: 'delivery',
        firestoreId: 'delivery',
        name: 'Delivery',
        icon: 'truck',
        iconText: 'Delivery',
      };
      break;

    case '6.3': // Custom purchase method (test with a non-standard method)
      sale.purchaseMethod = purchaseMethods?.[2] || {
        id: 'custom-purchase',
        firestoreId: 'custom-purchase',
        name: 'Custom Purchase',
        icon: 'shopping-bag',
        iconText: 'Custom',
      };
      break;
  }

  return sale;
}

function applyDeliveryVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods, purchaseMethods } = testData;

  // Add a default product
  sale.cartItems = [createTestCartItem(products[0], storeId, 1)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.finalTotalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;
  sale.paymentMethod = paymentMethods[0];

  const deliveryMethod = purchaseMethods?.find(p => p.id?.includes('delivery') || p.firestoreId?.includes('delivery')) || {
    id: 'delivery',
    firestoreId: 'delivery',
    name: 'Delivery',
    icon: 'truck',
    iconText: 'Delivery',
  };

  switch (variation.id) {
    case '7.1': // No delivery
      sale.purchaseMethod = purchaseMethods?.find(p => p.id === 'shop-offline' || p.firestoreId === 'shop-offline') || {
        id: 'shop-offline',
        firestoreId: 'shop-offline',
        name: 'Shop',
        icon: 'house-user',
        iconText: 'Shop',
      };
      break;

    case '7.2': // Delivery paid by customer
      sale.purchaseMethod = deliveryMethod;
      sale.deliveryPrice = 1000;
      sale.deliveryPaidByCustomer = true;
      sale.delivery = {
        address: 'Test Address 123',
        phone: '+77001234567',
        comment: 'Test delivery',
      };
      sale.finalTotalCartPrice = sale.totalCartPrice + sale.deliveryPrice;
      break;

    case '7.3': // Delivery paid by seller
      sale.purchaseMethod = deliveryMethod;
      sale.deliveryPrice = 1000;
      sale.deliveryPaidByCustomer = false;
      sale.deliveryPaymentSourceWallet = 'kaspi';
      sale.delivery = {
        address: 'Test Address 456',
        phone: '+77009876543',
      };
      break;
  }

  return sale;
}

function applyOptionalFieldsVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods, priceTypes } = testData;

  // Add a default product
  sale.cartItems = [createTestCartItem(products[0], storeId, 1)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.finalTotalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;
  sale.paymentMethod = paymentMethods[0];

  switch (variation.id) {
    case '8.1': // With table number
      sale.tableNumber = '5';
      break;

    case '8.3': // With comment
      sale.comment = 'Test comment with special chars: !@#$%^&*()';
      break;

    case '8.5': // With fiscal info
      sale.fiscalInfo = {
        receiptId: 'TEST-123',
        fiscalSign: 'ABC123',
        timestamp: new Date().toISOString(),
      };
      break;

    case '8.6': // With modifier
      sale.modifier = {
        name: 'Service charge',
        value: 10,
        type: 'percentage',
      };
      sale.modifierComment = 'Applied service charge';
      break;

    case '5.2': // Custom price type
      if (priceTypes && priceTypes.length > 0) {
        sale.priceType = priceTypes[0];
        sale.cartItems[0].priceType = priceTypes[0].id;
      }
      break;
  }

  return sale;
}

function applyDateVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods } = testData;

  // Add a default product
  sale.cartItems = [createTestCartItem(products[0], storeId, 1)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.finalTotalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;
  sale.paymentMethod = paymentMethods[0];

  // Use actual Date objects for Firestore compatibility
  switch (variation.id) {
    case '10.1': // Current date/time (default)
      sale.saleDate = new Date();
      break;

    case '10.2': { // Yesterday
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(14, 30, 0, 0);
      sale.saleDate = yesterday;
      sale.dailyStatID = `test-${formatDateForDailyStat(yesterday)}-${storeId}`;
      break;
    }

    case '10.3': { // 7 days ago
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      weekAgo.setHours(10, 0, 0, 0);
      sale.saleDate = weekAgo;
      sale.dailyStatID = `test-${formatDateForDailyStat(weekAgo)}-${storeId}`;
      break;
    }

    case '10.4': { // 30 days ago
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      monthAgo.setHours(16, 45, 0, 0);
      sale.saleDate = monthAgo;
      sale.dailyStatID = `test-${formatDateForDailyStat(monthAgo)}-${storeId}`;
      break;
    }

    case '10.5': { // Beginning of current month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(9, 0, 0, 0);
      sale.saleDate = startOfMonth;
      sale.dailyStatID = `test-${formatDateForDailyStat(startOfMonth)}-${storeId}`;
      break;
    }

    case '10.6': { // End of previous month
      const endOfPrevMonth = new Date();
      endOfPrevMonth.setDate(0); // Last day of previous month
      endOfPrevMonth.setHours(23, 59, 0, 0);
      sale.saleDate = endOfPrevMonth;
      sale.dailyStatID = `test-${formatDateForDailyStat(endOfPrevMonth)}-${storeId}`;
      break;
    }

    case '10.7': { // Specific time (edge case: midnight)
      const midnight = new Date();
      midnight.setDate(midnight.getDate() - 1);
      midnight.setHours(0, 0, 0, 0);
      sale.saleDate = midnight;
      sale.dailyStatID = `test-${formatDateForDailyStat(midnight)}-${storeId}`;
      break;
    }

    case '10.8': { // Specific time (edge case: 23:59)
      const endOfDay = new Date();
      endOfDay.setDate(endOfDay.getDate() - 1);
      endOfDay.setHours(23, 59, 59, 999);
      sale.saleDate = endOfDay;
      sale.dailyStatID = `test-${formatDateForDailyStat(endOfDay)}-${storeId}`;
      break;
    }
  }

  return sale;
}

/**
 * Format date for dailyStatID (d-M-y format)
 */
function formatDateForDailyStat(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

/**
 * Apply lifecycle variation - these tests create a sale, then perform an operation on it
 * The variation.lifecycleAction defines what to do after creation
 */
function applyLifecycleVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods, clients } = testData;

  // Start with a basic sale that can be modified
  sale.cartItems = [createTestCartItem(products[0], storeId, 2)];
  sale.totalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.finalTotalCartPrice = sale.cartItems[0].finalItemPrice;
  sale.totalPurchasePrice = sale.cartItems[0].purchasePrice * sale.cartItems[0].quantity;

  // Store the lifecycle action metadata on the sale for later use
  sale._lifecycleAction = variation.lifecycleAction;
  sale._lifecycleData = {};

  switch (variation.id) {
    case '12.1': // Update comment
      sale.paymentMethod = paymentMethods[0];
      sale._lifecycleData = {
        updateFields: { comment: 'Updated comment after creation' },
      };
      break;

    case '12.2': // Update with fiscal info
      sale.paymentMethod = paymentMethods[0];
      sale._lifecycleData = {
        updateFields: {
          fiscalInfo: {
            receiptId: 'FISCAL-123',
            fiscalSign: 'SIGN-ABC',
            qrCode: 'https://fiscal.example.com/receipt/123',
            timestamp: new Date().toISOString(),
          },
        },
      };
      break;

    case '12.3': // Mark as paid (pay later -> paid)
      sale.paymentMethod = null; // Start unpaid
      sale._lifecycleData = {
        markPaid: true,
        paymentInfo: {
          paymentMethod: paymentMethods[0],
        },
      };
      break;

    case '12.4': { // Update cart items (change quantity)
      sale.paymentMethod = paymentMethods[0];
      const updatedCartItem = { ...sale.cartItems[0], quantity: 5 };
      updatedCartItem.finalItemPrice = updatedCartItem.price * 5;
      sale._lifecycleData = {
        updateFields: {
          cartItems: [updatedCartItem],
          totalCartPrice: updatedCartItem.finalItemPrice,
          finalTotalCartPrice: updatedCartItem.finalItemPrice,
          totalPurchasePrice: updatedCartItem.purchasePrice * 5,
        },
      };
      break;
    }

    case '12.5': // Add client to sale
      sale.paymentMethod = paymentMethods[0];
      if (clients.length > 0) {
        sale._lifecycleData = {
          updateFields: {
            clientId: clients[0].id,
            client: clients[0],
            clientPhone: clients[0].phoneNumber,
          },
        };
      }
      break;

    case '12.6': // Update table number
      sale.paymentMethod = paymentMethods[0];
      sale.tableNumber = '1';
      sale._lifecycleData = {
        updateFields: { tableNumber: '99' },
      };
      break;

    case '12.7': // Partial return
      sale.paymentMethod = paymentMethods[0];
      if (clients.length > 0) {
        sale.clientId = clients[0].id;
        sale.client = clients[0];
      }
      sale._lifecycleData = {
        createReturn: true,
        returnInfo: {
          returnType: 1, // Return with money
          totalReturnedAmount: sale.cartItems[0].price, // Return 1 item worth
          date: new Date(),
          updateStock: true,
        },
        returnedItems: [{
          ...sale.cartItems[0],
          isReturned: true,
          returnedQuantity: 1,
          returnedAmount: sale.cartItems[0].price,
        }],
      };
      break;

    case '12.8': // Full return
      sale.paymentMethod = paymentMethods[0];
      sale._lifecycleData = {
        createReturn: true,
        returnInfo: {
          returnType: 1,
          totalReturnedAmount: sale.finalTotalCartPrice,
          date: new Date(),
          updateStock: true,
        },
        returnedItems: sale.cartItems.map(item => ({
          ...item,
          isReturned: true,
          returnedQuantity: item.quantity,
          returnedAmount: item.finalItemPrice,
        })),
      };
      break;

    case '12.9': // Delete sale
      sale.paymentMethod = paymentMethods[0];
      sale._lifecycleData = {
        deleteSale: true,
      };
      break;

    case '12.10': // Update payment method
      sale.paymentMethod = paymentMethods[0];
      // Only meaningful if we have multiple payment methods
      if (paymentMethods.length > 1) {
        sale._lifecycleData = {
          updateFields: {
            paymentMethod: paymentMethods[1],
          },
        };
      } else {
        // Skip this test variation if only one payment method
        sale._lifecycleData = {
          skip: true,
          skipReason: 'Only one payment method available',
        };
      }
      break;
  }

  return sale;
}

/**
 * Apply cascading variation - these tests verify side effects in related tables
 */
function applyCascadingVariation(sale, variation, testData) {
  const { products, storeId, paymentMethods, clients } = testData;

  // Default: add products so we can track movements/stock
  sale.cartItems = products.slice(0, 2).map((p, i) =>
    createTestCartItem(p, storeId, i + 1)
  );
  sale.totalCartPrice = sale.cartItems.reduce((sum, item) => sum + (item.finalItemPrice || 0), 0);
  sale.finalTotalCartPrice = sale.totalCartPrice;
  sale.totalPurchasePrice = sale.cartItems.reduce((sum, item) =>
    sum + ((item.purchasePrice || 0) * (item.quantity || 0)), 0
  );

  // Default payment method for most cascading tests
  sale.paymentMethod = paymentMethods[0];
  sale._cascadingFocus = variation.cascadingFocus;

  switch (variation.id) {
    case 'S_C1': // Client stats
      if (clients.length > 0) {
        const client = clients[0];
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
        // Store pre-sale values for comparison
        sale._preClientStats = {
          totalPurchasedValue: client.totalPurchasedValue || 0,
          purchaseCount: client.purchaseCount || 0,
        };
      } else {
        sale._lifecycleData = { skip: true, skipReason: 'No clients available' };
      }
      break;

    case 'S_C2': // Bonus usage
      if (clients.length > 0) {
        const client = { ...clients[0], bonusValue: 100 };
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
        sale.cashbackEnabled = true;
        sale.bonusReduction = 50; // Use 50 bonus points
        sale.bonusAction = 1; // Use bonus
        sale.finalTotalCartPrice = sale.totalCartPrice - sale.bonusReduction;
        sale._preBonusBalance = client.bonusValue || 100;
      } else {
        sale._lifecycleData = { skip: true, skipReason: 'No clients available' };
      }
      break;

    case 'S_C3': // Punch card
      if (clients.length > 0) {
        const client = clients[0];
        sale.clientId = client.id;
        sale.client = client;
        sale.clientPhone = client.phoneNumber;
        sale.punchCardEnabled = true;
        sale.punchCardCapacity = 7;
        sale.cartItems[0].punchCardEnabled = true;
        sale._prePunchCardValue = client.punchCardValue || 0;
      } else {
        sale._lifecycleData = { skip: true, skipReason: 'No clients available' };
      }
      break;

    case 'S_C4': // Movements
    case 'S_C5': // Stock
    case 'S_C7': // Daily stats
      // Default setup is sufficient
      break;

    case 'S_C6': // Transaction
      // Already have paymentMethod set, sale is paid by default
      break;

    case 'S_C8': // Delete reversals
      sale._lifecycleAction = 'delete';
      sale._lifecycleData = { deleteSale: true, verifyCascadingDeletes: true };
      break;
  }

  return sale;
}

/**
 * Execute lifecycle action after sale creation
 */
async function executeLifecycleAction(db, saleRef, saleData, testData) {
  const action = saleData._lifecycleAction;
  const data = saleData._lifecycleData;

  if (!action || !data) return { actionPerformed: null };

  // Handle skip case
  if (data.skip) {
    return { actionPerformed: 'skip', skipReason: data.skipReason };
  }

  // Wait a bit for initial sync
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    if (data.updateFields) {
      // Simple update
      await updateSale(saleRef, data.updateFields);
      return { actionPerformed: 'update', fields: Object.keys(data.updateFields) };
    }

    if (data.markPaid) {
      // Mark as paid - this is more complex as it creates a transaction
      await markSalePaid(db, { ...saleData, ref: saleRef }, data.paymentInfo);
      return { actionPerformed: 'markPaid' };
    }

    if (data.createReturn) {
      // Create a return
      const returnData = {
        ...saleData,
        returnInfo: data.returnInfo,
        cartItems: data.returnedItems,
      };
      await saveSaleReturn(db, saleRef, returnData, saleRef.id, saleData.cashierUID);
      // Wait extra time for the async Supabase dual-write to complete
      // nonBlockingApiCall can take 3-5 seconds with retries
      await new Promise(resolve => setTimeout(resolve, 5000));
      return { actionPerformed: 'createReturn' };
    }

    if (data.deleteSale) {
      // Delete the sale
      await deleteSale(db, saleRef, saleData, testData.uid);
      // Wait extra time for the async Supabase dual-write to complete
      // nonBlockingApiCall can take 3-5 seconds with retries
      await new Promise(resolve => setTimeout(resolve, 5000));
      return { actionPerformed: 'delete' };
    }
  } catch (error) {
    console.error('Lifecycle action failed:', error.message);
    return { actionPerformed: action, error: error.message };
  }

  return { actionPerformed: null };
}

/**
 * Create a test cart item from a product
 */
function createTestCartItem(product, storeId, quantity) {
  const salePrice = product.salePrices?.[storeId] || product.price || 1000;
  const purchasePrice = product.purchasePrice || 500;

  return {
    id: product.id,
    name: product.name || 'Test Product',
    quantity,
    price: salePrice,
    salePrices: product.salePrices || { [storeId]: salePrice },
    finalItemPrice: salePrice * quantity,
    itemPriceWithoutModifiers: salePrice * quantity,
    purchasePrice,
    punchCardEnabled: product.punchCardEnabled || false,
    ref: product.ref,
  };
}

/**
 * All test variations to run
 */
export const TEST_VARIATIONS = [
  // Client variations
  { id: '1.1', category: 'client', name: 'No client' },
  { id: '1.2', category: 'client', name: 'With client (no loyalty)' },
  { id: '1.3', category: 'client', name: 'With client + cashback' },
  { id: '1.4', category: 'client', name: 'With client + bonus reduction' },
  { id: '1.5', category: 'client', name: 'With client + punch card' },
  { id: '1.6', category: 'client', name: 'With client + punch card reduction' },
  { id: '1.7', category: 'client', name: 'With client + loyalty discount' },

  // Payment variations
  { id: '2.1', category: 'payment', name: 'Cash payment' },
  { id: '2.2', category: 'payment', name: 'Card/Kaspi payment' },
  { id: '2.3', category: 'payment', name: 'Split payment' },
  { id: '2.4', category: 'payment', name: 'Pay later (no payment)' },

  // Cart variations
  { id: '3.1', category: 'cart', name: 'Single product' },
  { id: '3.2', category: 'cart', name: 'Multiple products' },
  { id: '3.3', category: 'cart', name: 'Service only' },
  { id: '3.4', category: 'cart', name: 'Mixed (products + services)' },
  { id: '3.5', category: 'cart', name: 'Product with variant' },
  { id: '3.6', category: 'cart', name: 'Punch card enabled item' },
  { id: '3.7', category: 'cart', name: 'Large quantity (100)' },

  // Discount variations
  { id: '4.1', category: 'discount', name: 'No discount' },
  { id: '4.2', category: 'discount', name: 'Percentage discount (10%)' },
  { id: '4.3', category: 'discount', name: 'Fixed amount discount' },
  { id: '4.4', category: 'discount', name: 'Total discount fields' },

  // Price type
  { id: '5.2', category: 'optional', name: 'Custom price type' },

  // Purchase method variations
  { id: '6.1', category: 'purchaseMethod', name: 'Shop offline (in-store)' },
  { id: '6.2', category: 'purchaseMethod', name: 'Delivery purchase method' },
  { id: '6.3', category: 'purchaseMethod', name: 'Custom purchase method' },

  // Delivery variations
  { id: '7.1', category: 'delivery', name: 'No delivery (shop offline)' },
  { id: '7.2', category: 'delivery', name: 'Delivery paid by customer' },
  { id: '7.3', category: 'delivery', name: 'Delivery paid by seller' },

  // Optional fields
  { id: '8.1', category: 'optional', name: 'With table number' },
  { id: '8.3', category: 'optional', name: 'With comment' },
  { id: '8.5', category: 'optional', name: 'With fiscal info' },
  { id: '8.6', category: 'optional', name: 'With modifier' },

  // Date variations
  { id: '10.1', category: 'date', name: 'Current date/time' },
  { id: '10.2', category: 'date', name: 'Yesterday' },
  { id: '10.3', category: 'date', name: '7 days ago' },
  { id: '10.4', category: 'date', name: '30 days ago' },
  { id: '10.5', category: 'date', name: 'Beginning of current month' },
  { id: '10.6', category: 'date', name: 'End of previous month' },
  { id: '10.7', category: 'date', name: 'Midnight (00:00)' },
  { id: '10.8', category: 'date', name: 'End of day (23:59)' },

  // Lifecycle variations (create -> update/delete -> verify)
  { id: '12.1', category: 'lifecycle', name: 'Update comment', lifecycleAction: 'update' },
  { id: '12.2', category: 'lifecycle', name: 'Update with fiscal info', lifecycleAction: 'update' },
  { id: '12.3', category: 'lifecycle', name: 'Mark as paid (pay later)', lifecycleAction: 'markPaid' },
  { id: '12.4', category: 'lifecycle', name: 'Update cart items', lifecycleAction: 'update' },
  { id: '12.5', category: 'lifecycle', name: 'Add client to sale', lifecycleAction: 'update' },
  { id: '12.6', category: 'lifecycle', name: 'Update table number', lifecycleAction: 'update' },
  { id: '12.7', category: 'lifecycle', name: 'Partial return', lifecycleAction: 'return' },
  { id: '12.8', category: 'lifecycle', name: 'Full return', lifecycleAction: 'return' },
  { id: '12.9', category: 'lifecycle', name: 'Delete sale', lifecycleAction: 'delete' },
  { id: '12.10', category: 'lifecycle', name: 'Update payment method', lifecycleAction: 'update' },

  // Cascading write verifications (P0)
  { id: 'S_C1', category: 'cascading', name: 'Sale with client - verify client stats', cascadingFocus: 'clientStats' },
  { id: 'S_C2', category: 'cascading', name: 'Sale with bonus usage', cascadingFocus: 'bonusUsage' },
  { id: 'S_C3', category: 'cascading', name: 'Sale with punch card', cascadingFocus: 'punchCard' },
  { id: 'S_C4', category: 'cascading', name: 'Verify movements created', cascadingFocus: 'movements' },
  { id: 'S_C5', category: 'cascading', name: 'Verify stock decremented', cascadingFocus: 'stock' },
  { id: 'S_C6', category: 'cascading', name: 'Paid sale - verify transaction', cascadingFocus: 'transaction' },
  { id: 'S_C7', category: 'cascading', name: 'Verify daily stats updated', cascadingFocus: 'dailyStats' },
  { id: 'S_C8', category: 'cascading', name: 'Delete sale - verify cascading deletes', cascadingFocus: 'deleteReversals', lifecycleAction: 'delete' },
];

/**
 * Run a single test variation
 */
async function runSingleTest(db, variation, testData, options = {}) {
  const { cleanup = true, delayMs = 5000, logSessionId = null } = options;

  // Build headers for API calls if logSessionId is provided
  const apiConfig = logSessionId ? { headers: { 'x-log-session': logSessionId } } : {};

  console.log(`\n--- Running test ${variation.id}: ${variation.name} ---`);

  try {
    // Generate test sale
    const saleData = generateTestSale(variation, testData);

    // Create sale via frontend code (this triggers dual-write)
    const saleRef = await saveNewSale(db, saleData);
    const firestoreId = saleRef.id;

    console.log(`Created sale: ${firestoreId}`);

    // Wait for Supabase write to complete
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // Handle skipped tests (from cascading variations)
    if (saleData._lifecycleData?.skip) {
      console.log(`⏭️ SKIP: ${variation.name} - ${saleData._lifecycleData.skipReason}`);
      testResults.skipped.push({ variation, reason: saleData._lifecycleData.skipReason });
      return null;
    }

    // For lifecycle tests (or cascading with delete), perform the action after initial creation
    let lifecycleResult = null;
    if (variation.category === 'lifecycle' || saleData._lifecycleAction) {
      console.log(`Performing lifecycle action: ${variation.lifecycleAction || saleData._lifecycleAction}`);
      lifecycleResult = await executeLifecycleAction(db, saleRef, saleData, testData);
      console.log(`Lifecycle action result:`, lifecycleResult);

      // Handle skipped lifecycle tests
      if (lifecycleResult?.actionPerformed === 'skip') {
        console.log(`⏭️ SKIP: ${variation.name} - ${lifecycleResult.skipReason}`);
        testResults.skipped.push({ variation, reason: lifecycleResult.skipReason });
        // Cleanup the created sale
        if (cleanup) {
          try {
            await deleteSale(db, saleRef, saleData, testData.uid);
          } catch (e) { /* ignore */ }
        }
        return null;
      }

      // Wait for the update to sync
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    // Compare using the API
    let comparisonResult;
    let cascadingResult = null;

    // Special case: if sale was deleted, verify it's gone from both DBs
    if (lifecycleResult?.actionPerformed === 'delete') {
      comparisonResult = await verifyDeletion(firestoreId, saleRef, db);
    } else {
      try {
        // Compare endpoint is test-specific, keep untyped
        // Pass organizationId to help locate Firestore document if not yet in Supabase
        const compareUrl = `/sales/compare/${firestoreId}?organizationId=${testData.organizationId}`;
        const response = await api.get(compareUrl, apiConfig);
        comparisonResult = response.data;
      } catch (error) {
        console.warn('Could not use compare API, falling back to manual comparison');
        comparisonResult = await manualCompare(firestoreId, testData);
      }

      // For lifecycle tests, add lifecycle-specific validation
      if (variation.category === 'lifecycle' && comparisonResult.matches) {
        const lifecycleValidation = validateLifecycleResult(variation, saleData, comparisonResult);
        if (!lifecycleValidation.valid) {
          comparisonResult.matches = false;
          comparisonResult.differences.push(...lifecycleValidation.differences);
        }
      }
    }

    // For cascading tests, verify the cascading writes
    if (variation.category === 'cascading') {
      console.log(`Verifying cascading writes (focus: ${variation.cascadingFocus})...`);
      console.log(`Full comparisonResult keys:`, Object.keys(comparisonResult || {}));
      console.log(`Full comparisonResult:`, JSON.stringify(sanitizeForFirestore(comparisonResult), null, 2));
      console.log(`comparisonResult.supabaseId: ${comparisonResult?.supabaseId}`);

      // For delete reversals, use a different verification function
      if (variation.cascadingFocus === 'deleteReversals') {
        cascadingResult = await verifyCascadingDeletesForSale(firestoreId, saleData, testData);
      } else {
        cascadingResult = await verifyCascadingWrites(firestoreId, saleData, variation, testData, comparisonResult);
      }

      console.log(`Cascading verification result:`, JSON.stringify(sanitizeForFirestore(cascadingResult), null, 2));
    }

    // Determine overall pass/fail including cascading results
    const mainPassed = comparisonResult.matches;
    const cascadingPassed = cascadingResult ? (cascadingResult.success !== undefined ? cascadingResult.success : cascadingResult.matches) : true;
    const overallPassed = mainPassed && cascadingPassed;

    // Log result
    if (overallPassed) {
      console.log(`✅ PASS: ${variation.name}`);
      testResults.passed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
        cascadingResult,
      });
    } else {
      console.log(`❌ FAIL: ${variation.name}`);
      if (!mainPassed) {
        console.log('Main comparison differences:', JSON.stringify(sanitizeForFirestore(comparisonResult.differences), null, 2));
      }
      if (!cascadingPassed && cascadingResult) {
        console.log('Cascading verification failed:', JSON.stringify(sanitizeForFirestore(cascadingResult), null, 2));
      }
      testResults.failed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
        cascadingResult,
      });
    }

    // Cleanup - delete the test sale (skip if already deleted in lifecycle test)
    if (cleanup && lifecycleResult?.actionPerformed !== 'delete') {
      try {
        // Fetch actual sale data from Firestore (has transactionId, cartItemRefs, etc.)
        const saleDoc = await saleRef.get();
        if (saleDoc.exists) {
          const firestoreData = saleDoc.data();
          // Merge Firestore data with original sale data - Firestore data takes priority
          // but we need purchaseMethod from original sale since dailyStatIncrementsForSales needs purchaseMethod.id
          // Start with saleData (has all the structured objects we need)
          // Then overlay Firestore-specific fields
          // Explicitly exclude payment fields from spread (we'll add them back with validation)
          const { secondPaymentMethod: _spm, secondTransactionId: _sti, paymentMethod: _pm, ...saleDataClean } = saleData;
          // Validate paymentMethod has required structure for dailyStatIncrementsForSales
          const pm = saleData.paymentMethod;
          const pmValid = pm?.id && pm?.destinationWalletForStore?.[saleData.store]?.id;

          const savedSaleData = {
            ...saleDataClean,
            ref: saleRef,
            // Take transactionId from Firestore (needed for cleanup)
            transactionId: firestoreData.transactionId,
            // Ensure purchaseMethod has id (take from original sale)
            purchaseMethod: saleData.purchaseMethod || { id: 'shop-offline', firestoreId: 'shop-offline', name: 'Shop' },
            // Only include paymentMethod if it has valid structure (id + destinationWalletForStore[store].id)
            paymentMethod: pmValid ? pm : null,
            // Use cartItems from saleData (has refs) but add cartItemRef if exists
            cartItems: saleData.cartItems.map((item, idx) => ({
              ...item,
              cartItemRef: firestoreData.cartItems?.[idx]?.cartItemRef || item.cartItemRef,
            })),
          };
          // Only add secondTransactionId if it exists (avoid undefined in Firestore)
          if (firestoreData.secondTransactionId) {
            savedSaleData.secondTransactionId = firestoreData.secondTransactionId;
          }
          // Only add secondPaymentMethod if it exists and has proper structure
          // Need: id, destinationWalletForStore, and a wallet entry for this store
          const spm = saleData.secondPaymentMethod;
          if (spm?.id && spm?.destinationWalletForStore?.[saleData.store]?.id) {
            savedSaleData.secondPaymentMethod = spm;
          }
          await deleteSale(db, saleRef, savedSaleData, testData.uid);
          console.log(`Cleaned up sale: ${firestoreId}`);
        }
      } catch (cleanupError) {
        console.warn(`Failed to cleanup sale ${firestoreId}:`, cleanupError.message);
        // Fallback: just delete the sale document directly
        try {
          await saleRef.delete();
          console.log(`Fallback cleanup: deleted sale document ${firestoreId}`);
        } catch (e) {
          console.warn(`Fallback cleanup also failed: ${e.message}`);
        }
      }
    }

    return comparisonResult;
  } catch (error) {
    console.error(`❌ ERROR in test ${variation.id}:`, error.message);
    console.error(`Stack trace:`, error.stack);
    testResults.failed.push({
      variation,
      error: error.message,
      stack: error.stack,
    });
    return null;
  }
}

/**
 * Verify that a sale has been deleted from both databases
 * Uses retry logic because Supabase delete happens asynchronously via nonBlockingApiCall
 */
async function verifyDeletion(firestoreId, saleRef, db) {
  const differences = [];
  // Increase retries: nonBlockingApiCall has 3 retries with exponential backoff (1s, 2s, 4s = 7s max)
  // Plus backend deleteSale can retry up to 15 times if sale not found yet
  // Total worst case: ~25 seconds, use 15 retries with 2s delay = 30s coverage
  const maxRetries = 15;
  const retryDelayMs = 2000;

  // Check Firestore
  try {
    const firestoreDoc = await saleRef.get();
    if (firestoreDoc.exists) {
      differences.push({
        field: 'firestore_deletion',
        supabaseValue: 'deleted',
        firestoreValue: 'still exists',
        severity: 'critical',
      });
    }
  } catch (error) {
    // Document not found is expected
  }

  // Check Supabase with retry logic (async delete may not be complete yet)
  // Use getSaleById which properly looks up by firestore_id, not getSales which doesn't support that filter
  let supabaseDeleted = false;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // getSaleById looks up by firestore_id first, then by UUID
      await salesApi.getSaleById(firestoreId);
      // If we get here, sale still exists - wait and retry
      if (attempt < maxRetries) {
        console.log(`  Supabase delete not complete yet, retrying in ${retryDelayMs}ms (attempt ${attempt}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      }
    } catch (error) {
      // 404 or any error = sale not found = deleted successfully
      supabaseDeleted = true;
      break;
    }
  }

  if (!supabaseDeleted) {
    differences.push({
      field: 'supabase_deletion',
      supabaseValue: 'still exists',
      firestoreValue: 'deleted',
      severity: 'critical',
    });
  }

  return {
    firestoreId,
    foundInSupabase: !supabaseDeleted,
    foundInFirestore: differences.some(d => d.field === 'firestore_deletion'),
    matches: differences.length === 0,
    differences,
    warnings: [],
  };
}

/**
 * Validate lifecycle-specific results
 */
function validateLifecycleResult(variation, saleData, comparisonResult) {
  const differences = [];

  // Get the expected updated values from _lifecycleData
  const lifecycleData = saleData._lifecycleData;
  if (!lifecycleData) {
    return { valid: true, differences: [] };
  }

  // For update operations, check that the fields were actually updated
  if (lifecycleData.updateFields) {
    // Note: The comparison API already compares Firestore vs Supabase
    // Here we just verify the lifecycle action was reflected
    console.log('Lifecycle update fields:', Object.keys(lifecycleData.updateFields));
  }

  // For return operations, verify return_info exists
  if (lifecycleData.createReturn) {
    // The sale should have return_info after the return
    // This is already checked in the main comparison
    console.log('Return operation validated');
  }

  return {
    valid: differences.length === 0,
    differences,
  };
}

// ============================================================================
// CASCADING WRITE VERIFICATION FUNCTIONS
// ============================================================================

/**
 * Main orchestrator for verifying cascading writes
 */
async function verifyCascadingWrites(firestoreId, saleData, variation, testData, comparisonResult = null) {
  const cascadingResults = {
    movements: null,
    stock: null,
    transaction: null,
    dailyStats: null,
    clientStats: null,
    bonusUsage: null,
    punchCard: null,
  };
  const focus = variation.cascadingFocus;
  // Get Supabase ID from comparison result for linked_doc_id queries
  const supabaseId = comparisonResult?.supabaseId;

  try {
    if (focus === 'movements' || focus === 'deleteReversals') {
      cascadingResults.movements = await verifyProductMovements(supabaseId, saleData, testData);
    }

    if (focus === 'stock' || focus === 'deleteReversals') {
      cascadingResults.stock = await verifyStockDecremented(saleData, testData);
    }

    if (focus === 'transaction' || focus === 'deleteReversals') {
      cascadingResults.transaction = await verifyTransactionCreated(supabaseId, saleData, testData);
    }

    if (focus === 'dailyStats' || focus === 'deleteReversals') {
      cascadingResults.dailyStats = await verifySaleDailyStatsUpdated(saleData, testData);
    }

    if (focus === 'clientStats' || focus === 'deleteReversals') {
      cascadingResults.clientStats = await verifyClientStatsUpdated(saleData, testData);
    }

    if (focus === 'bonusUsage') {
      cascadingResults.bonusUsage = await verifyBonusUsageUpdated(saleData, testData);
    }

    if (focus === 'punchCard') {
      cascadingResults.punchCard = await verifyPunchCardUpdated(saleData, testData);
    }
  } catch (error) {
    console.error('Error verifying cascading writes:', error.message);
    return {
      success: false,
      error: error.message,
      results: cascadingResults,
    };
  }

  // Determine overall pass/fail
  const relevantResults = Object.entries(cascadingResults)
    .filter(([key, value]) => value !== null)
    .map(([key, value]) => ({ key, ...value }));

  const allPassed = relevantResults.every(r => r.matches);

  return {
    success: allPassed,
    results: cascadingResults,
    details: relevantResults,
  };
}

/**
 * Verify product movements were created for the sale
 */
async function verifyProductMovements(supabaseId, saleData, testData) {
  const differences = [];
  const expectedItems = saleData.cartItems || [];

  if (!supabaseId) {
    return {
      matches: false,
      differences: [{ field: 'supabaseId', expected: 'uuid', actual: 'missing', severity: 'critical' }],
      error: 'No Supabase sale ID provided for movement verification',
    };
  }

  try {
    // Fetch movements for this sale from Supabase via API
    // linked_doc_id stores the Supabase UUID of the sale, not the Firestore ID
    const response = await api.get('/product-movements', {
      params: {
        linkedDocId: supabaseId,
        linkedDocType: 'sale',
        limit: 50,
      },
    });

    const movements = response.data || [];

    // Check movement count
    if (movements.length !== expectedItems.length) {
      differences.push({
        field: 'movements.count',
        expected: expectedItems.length,
        actual: movements.length,
        severity: 'critical',
      });
    }

    // Verify each cart item has a corresponding movement
    for (const item of expectedItems) {
      const productId = item.id || item.product?.id;
      const expectedQuantity = -(item.quantity || 0); // Sales decrement stock

      const movement = movements.find(m =>
        m.product_firestore_id === productId || m.product_id === productId
      );

      if (!movement) {
        differences.push({
          field: `movement.${productId}`,
          expected: 'exists',
          actual: 'not found',
          severity: 'critical',
        });
      } else {
        // Verify movement type
        if (movement.type !== 'sale') {
          differences.push({
            field: `movement.${productId}.type`,
            expected: 'sale',
            actual: movement.type,
            severity: 'critical',
          });
        }

        // Verify quantity (should be negative for sales)
        if (Math.abs(Number(movement.quantity) - expectedQuantity) > 0.01) {
          differences.push({
            field: `movement.${productId}.quantity`,
            expected: expectedQuantity,
            actual: movement.quantity,
            severity: 'critical',
          });
        }
      }
    }

    return {
      matches: differences.length === 0,
      differences,
      movementsFound: movements.length,
      expectedCount: expectedItems.length,
    };
  } catch (error) {
    console.warn('Could not verify product movements:', error.message);
    return {
      matches: false,
      differences: [{ field: 'movements.api', expected: 'success', actual: error.message, severity: 'warning' }],
      error: error.message,
    };
  }
}

/**
 * Verify stock was decremented for sale items
 */
async function verifyStockDecremented(saleData, testData) {
  const differences = [];
  const { storeId } = testData;
  const cartItems = saleData.cartItems || [];

  try {
    // For each cart item, check that stock exists in Supabase
    // Note: We can't verify exact values without pre-sale baseline
    for (const item of cartItems) {
      const productId = item.id || item.product?.id;

      const response = await api.get('/products/stock', {
        params: {
          productId: productId,
          storeId: storeId,
        },
      });

      const stockData = response.data;
      if (!stockData) {
        differences.push({
          field: `stock.${productId}`,
          expected: 'exists in Supabase',
          actual: 'not found',
          severity: 'warning', // Warning because stock might be 0 or negative
        });
      }
    }

    return {
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      itemsChecked: cartItems.length,
    };
  } catch (error) {
    // Stock endpoint might not exist - use lenient handling
    console.warn('Could not verify stock (endpoint may not exist):', error.message);
    return {
      matches: true, // Lenient - don't fail if endpoint doesn't exist
      differences: [],
      warning: 'Stock verification skipped - endpoint not available',
    };
  }
}

/**
 * Verify transaction was created for paid sale
 */
async function verifyTransactionCreated(supabaseId, saleData, testData) {
  const differences = [];

  // Only paid sales should have transactions
  if (!saleData.paymentMethod) {
    return {
      matches: true,
      differences: [],
      note: 'Sale is unpaid - no transaction expected',
    };
  }

  if (!supabaseId) {
    return {
      matches: false,
      differences: [{ field: 'supabaseId', expected: 'uuid', actual: 'missing', severity: 'critical' }],
      error: 'No Supabase sale ID provided for transaction verification',
    };
  }

  try {
    // linked_doc_id stores the Supabase UUID of the sale, not the Firestore ID
    const response = await api.get('/transactions', {
      params: {
        linkedDocId: supabaseId,
        linkedDocType: 'sale',
        limit: 5,
      },
    });

    const transactions = response.data || [];

    if (transactions.length === 0) {
      differences.push({
        field: 'transaction',
        expected: 'exists',
        actual: 'not found',
        severity: 'critical',
      });
    } else {
      const transaction = transactions[0];

      // Verify transaction type (should be income for sale)
      if (transaction.type !== 'income') {
        differences.push({
          field: 'transaction.type',
          expected: 'income',
          actual: transaction.type,
          severity: 'critical',
        });
      }

      // Verify transaction value matches sale total
      const expectedValue = saleData.finalTotalCartPrice || saleData.totalCartPrice;
      if (Math.abs(Number(transaction.value) - expectedValue) > 0.01) {
        differences.push({
          field: 'transaction.value',
          expected: expectedValue,
          actual: transaction.value,
          severity: 'warning', // Warning because there might be rounding
        });
      }
    }

    return {
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      transactionsFound: transactions.length,
    };
  } catch (error) {
    console.warn('Could not verify transaction:', error.message);
    return {
      matches: false,
      differences: [{ field: 'transaction.api', expected: 'success', actual: error.message, severity: 'warning' }],
      error: error.message,
    };
  }
}

/**
 * Verify daily stats were updated for the sale
 */
async function verifySaleDailyStatsUpdated(saleData, testData) {
  // Note: Daily stats verification is complex because:
  // 1. We need to know the pre-sale values
  // 2. Multiple operations affect the same daily stat
  // For now, we just verify the daily_stats record exists

  const dailyStatId = saleData.dailyStatID;
  if (!dailyStatId) {
    return {
      matches: true,
      differences: [],
      note: 'No dailyStatID on sale - skipping verification',
    };
  }

  try {
    const response = await api.get('/reports/daily-stats', {
      params: {
        dailyStatId: dailyStatId,
        limit: 1,
      },
    });

    const stats = response.data || [];
    if (stats.length === 0) {
      return {
        matches: false,
        differences: [{
          field: 'daily_stats',
          expected: 'exists',
          actual: 'not found',
          severity: 'warning',
        }],
      };
    }

    return {
      matches: true,
      differences: [],
      statsFound: true,
    };
  } catch (error) {
    // Daily stats endpoint might have different path
    console.warn('Could not verify daily stats:', error.message);
    return {
      matches: true, // Lenient
      differences: [],
      warning: 'Daily stats verification skipped - endpoint not available',
    };
  }
}

/**
 * Verify client stats were updated after sale
 */
async function verifyClientStatsUpdated(saleData, testData) {
  if (!saleData.clientId) {
    return {
      matches: true,
      differences: [],
      note: 'Sale has no client - skipping client stats verification',
    };
  }

  const differences = [];
  const preStats = saleData._preClientStats || {};

  try {
    const response = await api.get(`/clients/${saleData.clientId}`);
    const client = response.data;

    if (!client) {
      differences.push({
        field: 'client',
        expected: 'exists in Supabase',
        actual: 'not found',
        severity: 'warning',
      });
    } else {
      // Verify purchase count increased
      const expectedPurchaseCount = (preStats.purchaseCount || 0) + 1;
      if (client.purchaseCount !== undefined && client.purchaseCount < expectedPurchaseCount) {
        differences.push({
          field: 'client.purchaseCount',
          expected: `>= ${expectedPurchaseCount}`,
          actual: client.purchaseCount,
          severity: 'warning',
        });
      }

      // Verify total purchased value increased
      const saleAmount = saleData.finalTotalCartPrice || saleData.totalCartPrice || 0;
      const expectedTotalValue = (preStats.totalPurchasedValue || 0) + saleAmount;
      if (client.totalPurchasedValue !== undefined && client.totalPurchasedValue < expectedTotalValue * 0.9) {
        differences.push({
          field: 'client.totalPurchasedValue',
          expected: `>= ${expectedTotalValue * 0.9}`,
          actual: client.totalPurchasedValue,
          severity: 'warning',
        });
      }
    }

    return {
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
    };
  } catch (error) {
    console.warn('Could not verify client stats:', error.message);
    return {
      matches: true, // Lenient
      differences: [],
      warning: 'Client stats verification skipped - endpoint not available',
    };
  }
}

/**
 * Verify bonus was deducted from client
 */
async function verifyBonusUsageUpdated(saleData, testData) {
  if (!saleData.clientId || !saleData.bonusReduction) {
    return {
      matches: true,
      differences: [],
      note: 'Sale has no bonus usage - skipping verification',
    };
  }

  const differences = [];
  const preBalance = saleData._preBonusBalance || 0;
  const expectedBalance = preBalance - saleData.bonusReduction;

  try {
    const response = await api.get(`/clients/${saleData.clientId}`);
    const client = response.data;

    if (!client) {
      differences.push({
        field: 'client',
        expected: 'exists',
        actual: 'not found',
        severity: 'warning',
      });
    } else if (client.bonusBalance !== undefined) {
      // Verify bonus balance decreased
      if (client.bonusBalance > expectedBalance + 1) { // Allow small margin
        differences.push({
          field: 'client.bonusBalance',
          expected: `<= ${expectedBalance}`,
          actual: client.bonusBalance,
          severity: 'warning',
        });
      }
    }

    return {
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
    };
  } catch (error) {
    console.warn('Could not verify bonus usage:', error.message);
    return {
      matches: true,
      differences: [],
      warning: 'Bonus verification skipped - endpoint not available',
    };
  }
}

/**
 * Verify punch card was incremented
 */
async function verifyPunchCardUpdated(saleData, testData) {
  if (!saleData.clientId || !saleData.punchCardEnabled) {
    return {
      matches: true,
      differences: [],
      note: 'Sale has no punch card - skipping verification',
    };
  }

  const differences = [];
  const preValue = saleData._prePunchCardValue || 0;
  const punchCardItems = (saleData.cartItems || []).filter(i => i.punchCardEnabled);
  const expectedIncrement = punchCardItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const expectedValue = preValue + expectedIncrement;

  try {
    const response = await api.get(`/clients/${saleData.clientId}`);
    const client = response.data;

    if (!client) {
      differences.push({
        field: 'client',
        expected: 'exists',
        actual: 'not found',
        severity: 'warning',
      });
    } else if (client.punchCardValue !== undefined) {
      if (client.punchCardValue < expectedValue - 1) { // Allow small margin
        differences.push({
          field: 'client.punchCardValue',
          expected: `>= ${expectedValue - 1}`,
          actual: client.punchCardValue,
          severity: 'warning',
        });
      }
    }

    return {
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
    };
  } catch (error) {
    console.warn('Could not verify punch card:', error.message);
    return {
      matches: true,
      differences: [],
      warning: 'Punch card verification skipped - endpoint not available',
    };
  }
}

/**
 * Verify cascading deletes happened after sale deletion
 */
async function verifyCascadingDeletesForSale(firestoreId, saleData, testData) {
  const results = {
    movements: null,
    transaction: null,
  };

  // Verify movements were deleted
  try {
    const movementsResponse = await api.get('/product-movements', {
      params: {
        linkedDocId: firestoreId,
        linkedDocType: 'sale',
        limit: 50,
      },
    });

    const movements = movementsResponse.data || [];
    results.movements = {
      matches: movements.length === 0,
      remaining: movements.length,
      expected: 0,
    };
  } catch (error) {
    results.movements = { matches: true, warning: 'Could not verify movement deletion' };
  }

  // Verify transaction was deleted (if sale was paid)
  if (saleData.paymentMethod) {
    try {
      const txResponse = await api.get('/transactions', {
        params: {
          linkedDocId: firestoreId,
          linkedDocType: 'sale',
          limit: 5,
        },
      });

      const transactions = txResponse.data || [];
      results.transaction = {
        matches: transactions.length === 0,
        remaining: transactions.length,
        expected: 0,
      };
    } catch (error) {
      results.transaction = { matches: true, warning: 'Could not verify transaction deletion' };
    }
  }

  const allMatch = Object.values(results)
    .filter(r => r !== null)
    .every(r => r.matches);

  return {
    matches: allMatch,
    results,
  };
}

/**
 * Manual comparison when API is not available
 */
async function manualCompare(firestoreId, testData) {
  // This is a simplified comparison - the API version is more thorough
  try {
    // Using typed API - returns SaleResponse[] directly
    const sales = await salesApi.getSales({ firestoreId });

    const supabaseSale = sales?.[0];

    return {
      firestoreId,
      supabaseId: supabaseSale?.id, // Add Supabase UUID for cascading verification
      foundInSupabase: !!supabaseSale,
      foundInFirestore: true,
      matches: !!supabaseSale,
      differences: supabaseSale ? [] : [{ field: 'sale', supabaseValue: null, firestoreValue: 'exists', severity: 'critical' }],
      warnings: [],
    };
  } catch (error) {
    return {
      firestoreId,
      foundInSupabase: false,
      foundInFirestore: true,
      matches: false,
      differences: [],
      warnings: [`Could not fetch from Supabase: ${error.message}`],
    };
  }
}

/**
 * Run all test variations
 */
export async function runSalesDualWriteTests(passedDb, options = {}) {
  const {
    variations = TEST_VARIATIONS,
    cleanup = true,
    delayMs = 3000,
    stopOnFailure = false,
    logSessionId = null,
  } = options;

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const userId = jotaiStore.get(userIdAtom);

  // Get db from atom if not passed
  const db = passedDb || jotaiStore.get(dbAtom);

  if (!db) {
    throw new Error('Database not initialized. Please wait for app to fully load.');
  }

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!storesDict || Object.keys(storesDict).length === 0) {
    throw new Error('Stores not loaded. Please wait for app to fully load.');
  }

  // Get first store
  const storeId = Object.keys(storesDict)[0];
  if (!storeId) {
    throw new Error('No stores found. Please create a store first.');
  }

  const store = storesDict[storeId];

  // Get products (need at least 3 for multi-product tests)
  const products = Object.values(productsDict || {}).slice(0, 5);
  if (products.length === 0) {
    throw new Error('No products found. Please create products first.');
  }

  // Debug: Check if products have refs
  console.log(`First product has ref: ${!!products[0]?.ref}`);
  if (!products[0]?.ref) {
    console.log(`First product keys: ${Object.keys(products[0] || {}).join(', ')}`);
    // Products from Jotai atom might not have refs - add them
    products.forEach(p => {
      if (!p.ref && p.id) {
        p.ref = db.collection('products').doc(p.id);
      }
    });
    console.log(`Added refs to products. First product now has ref: ${!!products[0]?.ref}`);
  }

  // Fetch a few clients from Firestore for testing
  let clients = [];
  try {
    const clientsSnapshot = await db.collection('clients').limit(5).get();
    clients = clientsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      ref: doc.ref,
    }));
    console.log(`Loaded ${clients.length} clients for testing`);
  } catch (error) {
    console.warn('Could not fetch clients:', error.message);
  }

  // Get payment methods from constants atom (NOT organization.paymentMethods)
  const constants = jotaiStore.get(constantsAtom);
  const allPaymentMethods = constants?.paymentMethods?.data || [];
  console.log(`Total payment methods: ${allPaymentMethods.length}`);

  // Debug: show what each payment method has
  allPaymentMethods.forEach((p, i) => {
    console.log(`Payment method ${i}: ${p.name || p.id}, isCash: ${p.isCash}, hasDestWallet: ${!!p.destinationWalletForStore}`);
  });

  // Check if any payment method has isCash=true
  const hasCashMethod = allPaymentMethods.some(p => p.isCash === true);
  console.log(`Has cash payment method: ${hasCashMethod}`);

  const paymentMethods = allPaymentMethods.filter(p =>
    p.destinationWalletForStore && Object.keys(p.destinationWalletForStore).length > 0
  );

  if (paymentMethods.length === 0) {
    // Try without the filter - add destinationWalletForStore if missing
    console.warn('No payment methods with destinationWalletForStore, creating fallback');

    // Get the default wallet for this store if available
    const storeWallets = store?.wallets || organization.wallets || [];
    const defaultWallet = storeWallets[0] || { id: 'default-wallet', name: 'Default' };

    if (allPaymentMethods.length > 0) {
      // Use existing methods but add destinationWalletForStore
      allPaymentMethods.forEach(p => {
        paymentMethods.push({
          ...p,
          destinationWalletForStore: p.destinationWalletForStore || { [storeId]: defaultWallet },
        });
      });
    } else {
      // Create minimal fallback payment methods
      paymentMethods.push({
        id: 'cash',
        name: 'Cash',
        isCash: true,
        destinationWalletForStore: { [storeId]: defaultWallet },
      });
      paymentMethods.push({
        id: 'card',
        name: 'Card',
        isCash: false,
        destinationWalletForStore: { [storeId]: defaultWallet },
      });
    }
    console.log(`Created ${paymentMethods.length} fallback payment methods`);
  }

  // Get purchase methods
  const purchaseMethods = organization.purchaseMethods || [
    { id: 'shop-offline', name: 'Shop' },
  ];

  // Get price types
  const priceTypes = organization.priceTypes || [];

  const testData = {
    organizationId: organization.id,
    storeId,
    store,
    products,
    clients,
    paymentMethods,
    purchaseMethods,
    priceTypes,
    uid: userId || 'test-uid',
  };

  console.log('\n========================================');
  console.log('SALES DUAL-WRITE TEST SUITE');
  console.log('========================================');
  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Store: ${store.name || storeId}`);
  console.log(`Store ID: ${storeId}`);
  console.log(`Products: ${products.length}`);
  console.log(`Clients: ${clients.length}`);
  console.log(`Payment methods with wallet for this store: ${paymentMethods.filter(p => p.destinationWalletForStore?.[storeId]).length}`);
  // Debug: Check if first payment method has wallet for this store
  if (paymentMethods.length > 0) {
    const pm = paymentMethods[0];
    const wallet = pm.destinationWalletForStore?.[storeId];
    console.log(`First payment method wallet for store: ${wallet ? JSON.stringify(sanitizeForFirestore(wallet)) : 'MISSING'}`);
  }
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    // Check if we have required data for this variation
    if (variation.category === 'client' && variation.id !== '1.1' && clients.length === 0) {
      console.log(`⏭️ SKIP: ${variation.name} (no clients available)`);
      testResults.skipped.push({ variation, reason: 'No clients available' });
      continue;
    }

    const result = await runSingleTest(db, variation, testData, { cleanup, delayMs, logSessionId });

    if (stopOnFailure && result && !result.matches) {
      console.log('\n⛔ Stopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Print summary
  console.log('\n========================================');
  console.log('TEST SUMMARY');
  console.log('========================================');
  console.log(`✅ Passed: ${testResults.passed.length}`);
  console.log(`❌ Failed: ${testResults.failed.length}`);
  console.log(`⏭️ Skipped: ${testResults.skipped.length}`);
  console.log('========================================\n');

  if (testResults.failed.length > 0) {
    console.log('FAILED TESTS:');
    testResults.failed.forEach(({ variation, result, error }) => {
      console.log(`  - ${variation.id}: ${variation.name}`);
      if (error) {
        console.log(`    Error: ${error}`);
      } else if (result?.differences) {
        result.differences.forEach(diff => {
          console.log(`    ${diff.field}: Supabase=${JSON.stringify(sanitizeForFirestore(diff.supabaseValue))} vs Firestore=${JSON.stringify(sanitizeForFirestore(diff.firestoreValue))}`);
        });
      }
    });
  }

  return testResults;
}

/**
 * Run a quick smoke test (just a few critical variations)
 */
export async function runQuickSmokeTest(db = null, options = {}) {
  const { logSessionId = null } = options;

  const quickVariations = [
    TEST_VARIATIONS.find(v => v.id === '1.1'), // No client
    TEST_VARIATIONS.find(v => v.id === '1.5'), // With punch card
    TEST_VARIATIONS.find(v => v.id === '2.3'), // Split payment
    TEST_VARIATIONS.find(v => v.id === '3.2'), // Multiple products
  ].filter(Boolean);

  return runSalesDualWriteTests(db, {
    variations: quickVariations,
    cleanup: true,
    delayMs: 3000,
    logSessionId,
  });
}

/**
 * Run only cascading write tests (P0 priority)
 * Tests verify side effects: movements, stock, transactions, daily_stats, client updates
 */
export async function runCascadingSalesTests(db = null, options = {}) {
  const cascadingVariations = TEST_VARIATIONS.filter(v => v.category === 'cascading');

  console.log('\n========================================');
  console.log('SALES CASCADING WRITE TESTS (P0)');
  console.log('========================================');
  console.log('These tests verify side effects created during sales:');
  cascadingVariations.forEach(v => console.log(`  - ${v.id}: ${v.name}`));
  console.log('========================================\n');

  return runSalesDualWriteTests(db, {
    variations: cascadingVariations,
    cleanup: true,
    delayMs: 5000, // Longer delay for cascading writes
    ...options,
  });
}

// Export cascading test variations for UI
export const CASCADING_TEST_VARIATIONS = TEST_VARIATIONS.filter(v => v.category === 'cascading');

export default {
  runSalesDualWriteTests,
  runQuickSmokeTest,
  runCascadingSalesTests,
  generateTestSale,
  TEST_VARIATIONS,
  CASCADING_TEST_VARIATIONS,
};
